import { Table, TableProps, Typography } from "antd";
import { useGetLinkedEmittedTitlesQuery } from "../../../redux/api/reconciliationsApiSlice";
import { useLinkedEmittedTable } from "../../../context/LinkedEmittedTableContext";
import { EmittedTitle } from "../../../types/reconciliation";
import React, { Key, useMemo } from "react";
import FilterBySearch from "../../tables/negotiationsTables/generalTable/FilterBySearch";
import { getColumnDateProps, getNumberRangeFilter } from "../../tables/negotiationsTables/columnsUtils";
import { isEmpty } from "lodash";
import { formatCurrency, printValue } from "utils/reconciliationTableUtils";
import BrokerFilter from "../../tables/negotiationsTables/BrokerFilter";

const { Text } = Typography;

export default function LinkedEmittedTitlesTable({ uuid }: { uuid: string }) {

  const {
    page,
    pageSize,
    ordering,
    collectiveContractCode,
    contractor,
    contractCode,
    credit,
    debit,
    documentName,
    productCode,
    startDate,
    taxableAmount,
    movementCode,
    brokerName,
    setPage,
    setPageSize,
    setOrdering,
    setCollectiveContractCode,
    setContractCode,
    setContractor,
    setCredit,
    setDebit,
    setDocumentName,
    setProductCode,
    setStartDate,
    setTaxableAmount,
    setMovementCode,
    setBrokerName
  } = useLinkedEmittedTable();
  const { data, isLoading, isFetching } = useGetLinkedEmittedTitlesQuery({
    uuid,
    page,
    page_size: pageSize,
    ...(!isEmpty(ordering) && { ordering: ordering }),
    ...(!isEmpty(credit) && {
      credit_min: credit?.at(0) as string,
      credit_max: credit?.at(1) as string
    }),
    ...(!isEmpty(debit) && {
      credit_min: debit?.at(0) as string,
      credit_max: debit?.at(1) as string
    }),
    ...(!isEmpty(taxableAmount) && {
      taxable_amount_min: taxableAmount?.at(0) as string,
      taxable_amount_max: taxableAmount?.at(1) as string
    }),
    //@ts-ignore
    ...(!isEmpty(brokerName) && { broker: brokerName.map(broker => broker.value)?.join(",") }),
    ...(!isEmpty(documentName) && { document_name: documentName?.join(",") }),
    ...(!isEmpty(movementCode) && { movement_code: movementCode?.join(",") }),
    ...(!isEmpty(productCode) && { product_code: productCode?.join(",") }),
    ...(!isEmpty(contractCode) && { contract_code: contractCode?.join(",") }),
    ...(!isEmpty(collectiveContractCode) && { collective_contract_code: collectiveContractCode?.join(",") }),
    ...(!isEmpty(contractor) && { contractor: contractor?.join(",") }),
    ...(!isEmpty(startDate) && {
      start_date_after: startDate?.at(0) as string,
      start_date_before: startDate?.at(1) as string
    })
  } ?? {});

  const columns: TableProps<EmittedTitle>["columns"] = useMemo(() => [
    {
      key: "document_name",
      dataIndex: "document_name",
      title: "Nome Documento",
      filteredValue: documentName,
      sorter: { multiple: 1 },
      filterDropdown: (props) => {
        return <FilterBySearch
          props={props}
          placeholder={"Filtra per nome documento..."} />;
      },
      ellipsis: true,
      render: (value) => printValue(value, "text")
    },
    {
      key: "movement_code",
      dataIndex: "movement_code",
      title: "Codice movimento",
      filteredValue: movementCode,
      sorter: { multiple: 1 },
      filterDropdown: (props) => {
        return <FilterBySearch
          props={props}
          placeholder={"Filtra per codice movimento..."} />;
      },
      ellipsis: true,
      render: (value) => printValue(value, "text")
    },
    {
      key: "broker_name",
      dataIndex: "broker_name",
      title: "Broker",
      filteredValue: brokerName,
      filterDropdown: (props) => BrokerFilter({ ...props, isReconciliationBroker: true }),
      sorter: { multiple: 1 },
      ellipsis: true,
      render: (value) => printValue(value, "text")
    },
    {
      key: "collective_contract_code",
      dataIndex: "collective_contract_code",
      title: "Numero polizza",
      filteredValue: collectiveContractCode,
      sorter: { multiple: 1 },
      filterDropdown: (props) => {
        return <FilterBySearch
          props={props}
          placeholder={"Filtra per noumero polizza..."} />;
      },
      ellipsis: true,
      render: (value) => printValue(value, "text")
    },
    {
      key: "contract_code",
      dataIndex: "contract_code",
      title: "Numero certificato",
      filteredValue: contractCode,
      sorter: { multiple: 1 },
      filterDropdown: (props) => {
        return <FilterBySearch
          props={props}
          placeholder={"Filtra per noumero certificato..."} />;
      },
      ellipsis: true,
      render: (value) => printValue(value, "text")
    },
    {
      key: "product_code",
      dataIndex: "product_code",
      title: "Codice prodotto",
      filteredValue: productCode,
      sorter: { multiple: 1 },
      filterDropdown: (props) => {
        return <FilterBySearch
          props={props}
          placeholder={"Filtra per codice prodotto..."} />;
      },
      ellipsis: true,
      render: (value) => printValue(value, "text")
    },
    {
      key: "contractor",
      dataIndex: "contractor",
      title: "Contraente",
      sorter: { multiple: 1 },
      filteredValue: contractor,
      ellipsis: true,
      filterDropdown: (props) => {
        return <FilterBySearch
          props={props}
          placeholder={"Filtra per il nome del contraente..."} />;
      },
      render: (value) => printValue(value, "text")
    },
    {
      key: "start_date",
      dataIndex: "start_date",
      title: "Data inizio",
      sorter: { multiple: 1 },
      filteredValue: startDate,
      ...getColumnDateProps("start_date", "day"),
      ellipsis: true,
      render: (value, record) => printValue(value, "date", { dateTemplate: "DD/MM/YYYY" })
    },
    {
      key: "taxable_amount",
      dataIndex: "taxable_amount",
      title: "Importo imponibile",
      className: "justify-right",
      sorter: { multiple: 1 },
      filteredValue: taxableAmount,
      ...getNumberRangeFilter(0, 1000000, 0.1, "euro"),
      ellipsis: true,
      render: (value) => printValue(value, "currency")
    },
    {
      key: "credit",
      dataIndex: "credit",
      title: "Premio lordo",
      className: "justify-right",
      sorter: { multiple: 1 },
      filteredValue: credit,
      ...getNumberRangeFilter(0, 1000000, 0.1, "euro"),
      ellipsis: true,
      render: (value) => printValue(value, "currency")
    },
    {
      key: "debit",
      dataIndex: "debit",
      title: "Provvigioni",
      className: "justify-right",
      sorter: { multiple: 1 },
      filteredValue: debit,
      ...getNumberRangeFilter(0, 1000000, 0.1, "euro"),
      ellipsis: true,
      render: (value) => printValue(value, "currency")
    }
  ], [brokerName, collectiveContractCode, contractCode, contractor, credit, debit, documentName, movementCode, productCode, startDate, taxableAmount]);

  const loading = isLoading || isFetching;
  return <Table
    columns={columns}
    loading={loading}
    dataSource={data?.results.data ?? []}
    rowKey={"uuid"}
    scroll={{ x: true }}
    pagination={{
      current: page,
      pageSize: pageSize,
      showSizeChanger: true,
      total: data?.count ?? 0,
      showTotal: (total, range) => `${range[0]}-${range[1]} di ${total} titoli`,
      onChange: (page, pageSize) => {
        setPage(page);
        setPageSize(pageSize);
      },
      position: ["bottomLeft"]
    }}
    onChange={(_, filters, sorter) => {
      // Gestione ordinamento
      const buildOrderingString = (sorterValue: any): string => {
        if (Array.isArray(sorterValue)) {
          return sorterValue
            .map(s => (s.order === "descend" ? `-${s.field}` : `${s.field}`))
            .join(",");
        } else if (sorterValue.order) {
          return sorterValue.order === "descend" ? `-${sorterValue.field}` : `${sorterValue.field}`;
        }
        return "";
      };

      // Applicazione ordinamento
      setOrdering(buildOrderingString(sorter));

      // Gestione filtri
      if (filters) {
        // Mappa delle funzioni setter per ogni tipo di filtro
        const filterSetters: Record<string, (value: Key[]) => void> = {
          collective_contract_code: setCollectiveContractCode,
          contract_code: setContractCode,
          contractor: setContractor,
          credit: setCredit,
          debit: setDebit,
          document_name: setDocumentName,
          product_code: setProductCode,
          start_date: setStartDate,
          taxable_amount: setTaxableAmount,
          movement_code: setMovementCode,
          broker_name: setBrokerName
        };

        // Applica i setter per ogni filtro
        Object.entries(filterSetters).forEach(([filterName, setter]) => {
          const filterValue = filters[filterName];
          setter(!isEmpty(filterValue) ? filterValue as Key[] : []);
        });
      }
    }}
    summary={() => (<>
      {!isEmpty(data) && (<>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0}><Text strong={true}>Totale premi lordi:</Text></Table.Summary.Cell>
          <Table.Summary.Cell index={1}>{formatCurrency(data.results.credit_sum)}</Table.Summary.Cell>
          <Table.Summary.Cell index={2}><Text strong={true}>Totale provvigioni:</Text></Table.Summary.Cell>
          <Table.Summary.Cell index={3}>{formatCurrency(data.results.debit_sum)}</Table.Summary.Cell>
        </Table.Summary.Row>
      </>)}
    </>)}
  />;
}